import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Shield, Users, Images, CalendarDays, Heart, Lock, Sparkles, CheckCircle2, ArrowRight } from 'lucide-react';
import ShareAppButton from '@/components/ShareAppButton';
import { shouldHideBetaListBadge } from '@/lib/platform';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_family-connect-195/artifacts/d7hehvno_Color%20logo%20with%20background.svg';
const HERO_IMG = 'https://images.unsplash.com/photo-1772723246543-213f2a4fc526?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGZhbWlseSUyMGRpdmVyc2UlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzY1NjA1Mjd8MA&ixlib=rb-4.1.0&q=85';

export default function LandingPage() {
  const { user } = useAuth();

  const features = [
    { icon: Images, title: 'Preserve Memories', desc: 'Photos, videos, albums, milestones, and "On This Day" — one lasting record of your family\'s story.' },
    { icon: Users, title: 'Include Every Generation', desc: 'Grandparents view-only, cousins can comment, distant relatives stay in the loop. Access per person.' },
    { icon: CalendarDays, title: 'Coordinate Family Life', desc: 'Shared calendar, birthdays, milestones, and reminders sync to Apple Calendar, Google, and Outlook.' },
    { icon: Heart, title: 'Stay Connected', desc: 'Everyday moments, reactions, and comments — feel present in each other\'s lives, even from miles away.' },
    { icon: Shield, title: 'Protect Your Family Space', desc: 'Invitation-only. No ads. No public profiles. No algorithmic feed.' },
    { icon: Lock, title: 'You Own Everything', desc: 'Your family\'s story belongs to you. Export anytime, delete anytime — data gone in 30 days.' },
  ];

  const testimonials = [
    { quote: "We're military. Kinsy is how my parents watch our kids grow up between duty stations. They open it every morning with coffee.", name: 'Sarah M.', role: 'Navy family, currently PCS' },
    { quote: "My in-laws are in India. This is the only way they see their grandkids grow up — without me putting their faces on Instagram.", name: 'Priya R.', role: 'Mom of 2, Seattle' },
    { quote: "Grandma just taps ❤️. That's it. No feed to scroll, no strangers, no ads. She's 82 and never misses a birthday.", name: 'James K.', role: 'Dad, Austin' },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="landing-page">
      {/* Nav */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Kinsy" className="h-8" />
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-stone-600">
            <a href="#features" className="hover:text-stone-900">Features</a>
            <a href="#how" className="hover:text-stone-900">How it works</a>
            <Link to="/pricing" className="hover:text-stone-900">Pricing</Link>
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <Button asChild className="bg-teal-600 hover:bg-teal-700 text-white rounded-full">
                <Link to="/" data-testid="nav-app-btn">Open app</Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="ghost" className="rounded-full hidden sm:inline-flex">
                  <Link to="/login" data-testid="nav-login-btn">Sign in</Link>
                </Button>
                <Button asChild className="bg-teal-600 hover:bg-teal-700 text-white rounded-full">
                  <Link to="/register" data-testid="nav-register-btn">Get started</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 pt-16 pb-20 md:pt-24 md:pb-28 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-flex items-center gap-2 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-3 py-1 mb-6">
            <Sparkles className="w-3.5 h-3.5" /> Invitation-only · Ad-free · Family-only
          </span>
          <h1 className="text-5xl lg:text-6xl font-bold text-stone-900 tracking-tight leading-[1.05] mb-6">
            Stay close,<br/>
            <span className="text-teal-600">wherever family lives.</span>
          </h1>
          <p className="text-lg text-stone-600 mb-4 max-w-lg">
            Kinsy helps families who live apart stay connected through a private shared history of photos, milestones, stories, and everyday moments.
          </p>
          <p className="text-sm text-stone-500 mb-8 max-w-lg">
            No public profiles. No ads. No algorithm. Just your family.
          </p>
          <div className="flex flex-wrap gap-3 mb-8">
            <Button asChild size="lg" className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8 h-12">
              <Link to={user ? '/' : '/register'} data-testid="hero-cta">
                {user ? 'Open your family space' : 'Start your family'} <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full px-8 h-12">
              <Link to="/pricing" data-testid="hero-pricing">See pricing</Link>
            </Button>
          </div>
          <div className="flex items-center gap-6 text-sm text-stone-600">
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-teal-500" /> Free forever plan</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-teal-500" /> No credit card</span>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-4 bg-gradient-to-tr from-teal-200 via-amber-100 to-stone-100 rounded-3xl opacity-50 blur-2xl" />
          <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-stone-200">
            <img src={HERO_IMG} alt="Family sharing moments" className="w-full h-[500px] object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 border border-stone-200 max-w-xs hidden sm:block">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                <Heart className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-stone-900">Grandma Marie</p>
                <p className="text-xs text-stone-500">reacted to your post</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-stone-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <div><div className="text-3xl font-bold">100%</div><div className="text-sm text-stone-400 mt-1">Private</div></div>
          <div><div className="text-3xl font-bold">0</div><div className="text-sm text-stone-400 mt-1">Ads or trackers</div></div>
          <div><div className="text-3xl font-bold">∞</div><div className="text-sm text-stone-400 mt-1">Photos on paid plans</div></div>
          <div><div className="text-3xl font-bold">3</div><div className="text-sm text-stone-400 mt-1">Calendar integrations</div></div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-6xl mx-auto px-4 py-20 md:py-28">
        <div className="text-center mb-14">
          <h2 className="text-4xl md:text-5xl font-bold text-stone-900 tracking-tight mb-4">
            Bring distant family into everyday life
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            Help grandparents, relatives, and loved ones feel present in your family&apos;s life — even from another state or country.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <div key={i} className="card-soft hover:shadow-md transition-shadow" data-testid={`feature-${i}`}>
              <div className="w-12 h-12 bg-teal-50 rounded-2xl flex items-center justify-center mb-4">
                <f.icon className="w-6 h-6 text-teal-600" />
              </div>
              <h3 className="text-xl font-semibold text-stone-900 mb-2">{f.title}</h3>
              <p className="text-stone-600 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-stone-50 py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900 tracking-tight mb-4">
              How it works
            </h2>
            <p className="text-lg text-stone-600">Up and running in under 3 minutes.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { n: '01', t: 'Create your private family space', d: 'Sign up and name your family. You\'re the admin — no strangers, no ads, ever.' },
              { n: '02', t: 'Bring your family closer', d: 'Invite grandparents, siblings, cousins. Choose per person: full member or view-only.' },
              { n: '03', t: 'Share the everyday moments', d: 'Photos, milestones, events, "on this day" memories — build your family\'s lasting shared history.' },
            ].map((step, i) => (
              <div key={i} className="relative" data-testid={`step-${i}`}>
                <div className="text-6xl font-bold text-teal-500/20 mb-3">{step.n}</div>
                <h3 className="text-xl font-semibold text-stone-900 mb-2">{step.t}</h3>
                <p className="text-stone-600">{step.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-6xl mx-auto px-4 py-20 md:py-28">
        <div className="text-center mb-14">
          <h2 className="text-4xl md:text-5xl font-bold text-stone-900 tracking-tight mb-4">
            For families who live apart
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            Military families, grandparents in another country, blended families, cousins across states — Kinsy keeps everyone close.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div key={i} className="card-soft" data-testid={`testimonial-${i}`}>
              <div className="text-teal-500 text-4xl font-serif leading-none mb-2">&ldquo;</div>
              <p className="text-stone-700 mb-6 leading-relaxed">{t.quote}</p>
              <div className="border-t border-stone-100 pt-4">
                <p className="font-semibold text-stone-900">{t.name}</p>
                <p className="text-sm text-stone-500">{t.role}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-4 pb-24">
        <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-3xl p-12 md:p-16 text-center text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 25% 25%, white 1px, transparent 1px), radial-gradient(circle at 75% 75%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
          <div className="relative">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
              Start your family&apos;s lasting story
            </h2>
            <p className="text-teal-50 text-lg mb-8 max-w-xl mx-auto">
              Free forever for up to 50 photos and 5 members. Invitation-only. No ads. Ever.
            </p>
            <Button asChild size="lg" className="bg-white text-teal-700 hover:bg-stone-50 rounded-full px-10 h-12 font-semibold">
              <Link to={user ? '/' : '/register'} data-testid="cta-bottom">
                {user ? 'Open Kinsy' : 'Get started free'} <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* As featured on — third-party social proof. Hidden inside the iOS
          app to avoid the App Review team flagging beta/waitlist messaging. */}
      {!shouldHideBetaListBadge() && (
      <section className="border-t border-stone-200 py-10 bg-white">
        <div className="max-w-6xl mx-auto px-4 flex flex-col items-center gap-4">
          <span className="text-xs font-semibold tracking-widest uppercase text-stone-400">
            As featured on
          </span>
          <a
            href="https://betalist.com/startups/kinsy?utm_campaign=badge-kinsy&utm_medium=badge&utm_source=badge-featured"
            target="_blank"
            rel="noreferrer"
            aria-label="Kinsy on BetaList"
            data-testid="betalist-badge"
          >
            <img
              src="https://betalist.com/badges/featured?id=167100&theme=color"
              alt="Kinsy - Share family photos and events in a private, ad-free space | BetaList"
              width="156"
              height="54"
              loading="lazy"
              style={{ width: 156, height: 54 }}
            />
          </a>
        </div>
      </section>
      )}

      {/* Footer */}
      <footer className="border-t border-stone-200 py-10">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-stone-500">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Kinsy" className="h-6" />
            <span>© 2026 Kinsy. Built for families.</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-stone-900">Privacy</Link>
            <Link to="/terms" className="hover:text-stone-900">Terms</Link>
            <Link to="/pricing" className="hover:text-stone-900">Pricing</Link>
            <Link to="/llm-info" className="hover:text-stone-900" data-testid="footer-llm-info-link">For AI</Link>
            <a href="mailto:contact@kinsy.io" className="hover:text-stone-900">Contact</a>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 mt-6 flex justify-center">
          <ShareAppButton
            variant="outline"
            className="rounded-full border-teal-200 text-teal-700 hover:bg-teal-50"
            label="Share Kinsy with a family via text"
            testId="footer-share-app-button"
          />
        </div>
      </footer>
    </div>
  );
}
